import { ThemeProvider } from "@emotion/react";
import { JourneyContext } from "@flow/common/types";
import { IntlProvider } from "@flow/lib/i18n";
import { analyticsAndMarketingCookieName } from "@flow/plugins/civic_uk_cookie_control/frontend/member/CookieControl";
import {
  CookieConsentContext,
  CookieConsents,
} from "@flow/plugins/civic_uk_cookie_control/frontend/member/CookieControlContext";
import { Box, CircularProgress, Container, CssBaseline, useMediaQuery } from "@mui/material";
import axios from "axios";
import { usePostHog } from "posthog-js/react";
import React, { useState } from "react";
import { FeatureFlagContext } from "../FeatureFlagContext";
import { PostHogProviderWrapper } from "../PostHogProviderWrapper";
import { memberTheme } from "../memberTheme";
import { Footer } from "./Footer";
import Header from "./Header";
import { MemberJourney } from "./MemberJourney";
import { MemberNonJourney } from "./MemberNonJourney";
import ToggleSwitch from "./Switch";
import { UtilityComponentLoader } from "./UtilityComponentLoader";

const Root: React.FC<{ isJourney: boolean }> = ({ isJourney }) => {
  /* Early Access is a permission granted to certain users (see core_permissions.ts).
   * The setter is passed to child components that fetch Early Access data from Flow
   * backend and call back here to Root.tsx to set the state.
   * */
  const [isEarlyAccessStaffUser, setIsEarlyAccessStaffUser] = useState(false);
  const user_language = DEFAULT_LANGUAGE;
  const [displayEarlyAccessFeatures, setDisplayEarlyAccessFeatures] = useState(false);
  const [cookieConsents, setCookieConsents] = useState<CookieConsents | undefined>(undefined);
  const [translation, setTranslation] = React.useState<any>({});
  const isMobile = useMediaQuery(memberTheme.breakpoints.down("sm"));
  const posthog = usePostHog();

  const [posthogFeatureFlagsLoaded, setPosthogFeatureFlagsLoaded] = useState(false);

  // seems the ref is required here to ensure up-to-date value is accessed in the onFeatureFlags callback
  const isEarlyAccessStaffUserRef = React.useRef(isEarlyAccessStaffUser);
  isEarlyAccessStaffUserRef.current = isEarlyAccessStaffUser;

  React.useEffect(() => {
    posthog?.onFeatureFlags(() => {
      setPosthogFeatureFlagsLoaded(true);
    });
  }, [posthog]);

  // Always default to displaying Early Access features for staff users with the permission.
  // the isEarlyAccessStaffUser state should only change at-most-once per page load, which is why it's in its own useEffect.
  React.useEffect(() => setDisplayEarlyAccessFeatures(isEarlyAccessStaffUser), [isEarlyAccessStaffUser]);

  // slightly convoluted but the ref is required here to ensure that the cookieConsents value is up to date when accessed in the setTimeout handler below.
  const cookieConsentsRef = React.useRef(cookieConsents);
  cookieConsentsRef.current = cookieConsents;

  // as above, the ref is required here to ensure up-to-date value is accessed in the setTimeout handler
  const posthogFeatureFlagsLoadedRef = React.useRef(posthogFeatureFlagsLoaded);
  posthogFeatureFlagsLoadedRef.current = posthogFeatureFlagsLoaded;

  React.useEffect(() => {
    loadLanguage(user_language), [user_language];
    // A paranoid fallback to prevent eternal loading spinner, in the case that proper mechanisms don't fire
    // Intention is to remove this in future Posthog and feature flag development
    const loadingTimeoutId = setTimeout(() => {
      if (cookieConsentsRef.current === undefined) {
        console.warn("Cookie consent loading timeout reached.");
        setCookieConsents({ [analyticsAndMarketingCookieName]: false });
      }
      if (!posthogFeatureFlagsLoadedRef.current) {
        console.warn("Posthog feature flag loading timeout reached.");
        setPosthogFeatureFlagsLoaded(true);
      }
    }, 5 * 1000);
    // ensure the timeout is cleared if the component is unmounted
    return () => clearTimeout(loadingTimeoutId);
  }, []);

  React.useEffect(() => {
    if (cookieConsents !== undefined && !cookieConsents?.[analyticsAndMarketingCookieName]) {
      // the user has not consented to analytics and marketing cookies and so Posthog is not active
      // and will never call the onFeatureFlags callback above. So we simlulate this by setting loaded state here.
      setPosthogFeatureFlagsLoaded(true);
    }
  }, [cookieConsents]);

  const renderMainContent = () => {
    if (isJourney) {
      return renderMemberJourneyComponent(setIsEarlyAccessStaffUser);
    } else {
      return (
        <MemberNonJourney
          initialRoute={window.location.hash.slice(1)}
          setIsEarlyAccessStaffUser={setIsEarlyAccessStaffUser}
        />
      );
    }
  };

  function loadLanguage(newLang: string) {
    // nothing to load for english
    if (newLang === "en") return;

    axios.get(`/i18n/${newLang}.json`).then((response) => {
      setTranslation(response.data);
    });
  }

  const fullScreenLoader = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh", // adjust as needed
      }}
    >
      <CircularProgress
        size={80}
        sx={{ color: memberTheme.palette.primary.main }}
        aria-label="The page is loading"
        title="The page is loading"
      />
    </Box>
  );

  return (
    <>
      <FeatureFlagContext.Provider value={{}}>
        <IntlProvider definition={translation}>
          <CookieConsentContext.Provider value={{ cookieConsents, setCookieConsents }}>
            <UtilityComponentLoader />
            {cookieConsents === undefined ? (
              fullScreenLoader
            ) : (
              <PostHogProviderWrapper>
                {!posthogFeatureFlagsLoaded ? (
                  fullScreenLoader
                ) : (
                  <ThemeProvider theme={memberTheme}>
                    <CssBaseline />
                    <Header />
                    <Container sx={{ flex: 2 }} component={"main"}>
                      {renderMainContent()}
                    </Container>

                    <Box
                      sx={{
                        position: "relative",
                      }}
                    >
                      <Footer />
                      {isEarlyAccessStaffUser && (
                        <Box
                          sx={{
                            width: "174px",
                            position: "absolute",
                            right: "16px",
                            bottom: "16px",
                            opacity: 1,
                            zIndex: 999,
                            height: "40px",
                            marginBottom: { md: displayEarlyAccessFeatures ? "25px" : "0" },
                            marginTop: isMobile ? "20px" : "0",
                          }}
                        >
                          <ToggleSwitch
                            switchValue={displayEarlyAccessFeatures}
                            toggle={setDisplayEarlyAccessFeatures}
                            label="Early Access UI"
                          />
                        </Box>
                      )}
                    </Box>
                  </ThemeProvider>
                )}
              </PostHogProviderWrapper>
            )}
          </CookieConsentContext.Provider>
        </IntlProvider>
      </FeatureFlagContext.Provider>
    </>
  );
};

function parseUrlInjectedJourneyContext(context: JourneyContext, urlSearchComponent: string) {
  urlSearchComponent.split("&").forEach((p) => {
    const [key, value] = p.split("=").map(decodeURIComponent);
    switch (key) {
      case "utm_source":
        context.utm.source = value;
        break;
      case "utm_medium":
        context.utm.medium = value;
        break;
      case "utm_campaign":
        context.utm.campaign = value;
        break;
      case "zip":
      case "postcode":
        context.member.postcode = value;
        break;
      case "email":
        context.member.email = value;
        break;
      case "member_guid":
        context.member.external_id = value;
        break;
      default:
        context.actionData[key] = value;
        break;
    }
  });
}

const renderMemberJourneyComponent = (handleSetIsEarlyAccessStaffUser: (value: boolean) => void) => {
  // 2^32
  const MAX_JOURNEY_SEED = 4294967296;
  const initialContext: JourneyContext = {
    // choose a new random seed for this user's journey
    seed: Math.floor(Math.random() * MAX_JOURNEY_SEED),
    member: {},
    actionData: {},
    utm: {},
  };

  /*
   * Data in the hash of the URL will be parsed:
   *   /act/some-campaign#member_guid=blah&return_uri=http://example.com/
   * or
   *   /act/some-campaign?member_guid=blah&return_uri=http://example.com/
   * to fill context.actionData with:
   *   {"member_guid":"blah","return_uri":"http://example.com/"}
   */
  parseUrlInjectedJourneyContext(initialContext, location.hash.slice(1));
  parseUrlInjectedJourneyContext(initialContext, location.search.slice(1));

  return (
    <MemberJourney
      journey_guid={JOURNEY_GUID}
      initialContext={initialContext}
      setIsEarlyAccessStaffUser={handleSetIsEarlyAccessStaffUser}
    />
  );
};

export default Root;
